import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import { alpha, styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

type Props = {
  delay?: string
  errorMessages: string[]
  isValid: boolean
  published: boolean
}

const getStatusText = (hasErrors: boolean, published: boolean) => {
  if (hasErrors) {
    return published
      ? 'You need to fix these errors before you can save updates to this campaign.'
      : 'You need to fix these errors before this campaign can go live.'
  }

  if (published) {
    return 'Your campaign updates are ready to save!'
  }

  return 'Your campaign is ready to go live!'
}

const CampaignStatus: React.FC<Props> = ({ delay, errorMessages, isValid, published }) => {
  const hasErrors = !isValid

  return (
    <Card id="campaign-status" data-testid="campaign-status" sx={{ mb: '2rem' }}>
      <CardHeader
        title={
          <StatusHeader hasErrors={hasErrors}>
            <Typography component="span" variant="h6">
              Campaign status:
            </Typography>
            {hasErrors ? <ErrorOutlineRoundedIcon /> : <CheckCircleRoundedIcon />}
          </StatusHeader>
        }
        sx={{ padding: '12px 24px' }}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {getStatusText(hasErrors, published)}
        </Typography>
        {hasErrors ? (
          <ErrorList>
            {(errorMessages.length ? errorMessages : ['This campaign has validation errors.']).map((message, i) => (
              <ErrorListItem key={i}>
                <Typography variant="body2">{message}</Typography>
              </ErrorListItem>
            ))}
          </ErrorList>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {published ? (
              'No campaign validation errors were found. Your updates can be saved.'
            ) : (
              <React.Fragment>
                When you click the <strong>Set Live</strong> button, this campaign will go live immediately.
                {delay && (
                  <React.Fragment>
                    {' '}
                    The first message will be sent <strong>{delay}</strong> after each contact is subscribed to this
                    campaign.
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

const StatusHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasErrors',
})<{ hasErrors: boolean }>(({ hasErrors, theme }) => ({
  alignItems: 'center',
  color: hasErrors ? theme.palette.error.main : theme.palette.success.main,
  display: 'flex',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: 22,
  },
}))

const ErrorList = styled('ul')(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.main, 0.04),
  border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(3),
  padding: theme.spacing(1, 2, 1, 4),
  '& li::marker': {
    color: theme.palette.error.main,
    fontSize: '1.15em',
  },
}))

const ErrorListItem = styled('li')(({ theme }) => ({
  paddingBottom: theme.spacing(0.75),
  paddingTop: theme.spacing(0.75),
}))

export default CampaignStatus
